
//     Init Variant Selects
//
//     Data attributes accept:
//     - data-disable-url-update: {bool} Disable url update on variant change (optional)
//
//     Usage:
//     <variant-radios data-section="{{ product.id }}" data-url="{{ product.url }}" data-disable-url-update></variant-radios>

import {handleize} from "../utils/shopify";

const TYPE_DEFAULT = 'regular';
const TYPE_CUSTOMIZABLE = 'customizable';
const CUSTOMIZABLE_HANDLE_SUFFIXE = '-custom';

export default class VariantRadios extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        // string
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.url;
        this.disableUrlUpdate = this.hasAttribute('disable-url-update') || false;
        this.eventOnChange = this.hasAttribute('event-on-change') || false;
        this.selectedOption = {};
        this.currentVariant = {};

        // Array
        this.availableOptions = [];
        const availableOptionsStr = this.getAttribute('data-options');
        if (this.availableOptions) {
            this.availableOptions = availableOptionsStr.split(',');
        }

        // DOM
        this.productForm = document.getElementById(`product-form-${this.sectionId}`);
        this.addButton = this.productForm.querySelector('[name="add"]');
        this.addButtonText = this.addButton.querySelector('span');

        // Select mode (customizable or not)
        this.type = this.getAttribute('data-type') || TYPE_DEFAULT;

        this.updateOptions();
        this.updateCurrentVariant();
        this.addEventListener('change', this.onVariantChange);

        if (this.currentVariant);

        // Check if the current variant is customizable when element is loaded
        // Also set as selected the correct label.
        this.updateForAttributeForInputs(this.type);
        if(this.currentVariant.options[0].includes('(custom)')) {
            this.setAttribute('data-type', TYPE_CUSTOMIZABLE);
            const checkedVariant = this.querySelector('input:checked');
            this.querySelector(`label[for="${checkedVariant.getAttribute('id')}"]`)?.classList.add('force-selected');

            // If the variant is custom but the non-custom variant is unavailable, we select the unavailable variant
            const nonCustomVariantId = checkedVariant.getAttribute('id').replace(CUSTOMIZABLE_HANDLE_SUFFIXE, '');
            const nonCustomVariantInputElement = this.querySelector(`input[id="${nonCustomVariantId}"`);
            if(nonCustomVariantInputElement && nonCustomVariantInputElement.classList.contains('product-variant-option--unavailable')) {
             const nonCustomVariantLabelElement = this.querySelector(`label[for="${nonCustomVariantId}"]`);
                nonCustomVariantLabelElement.classList.add('force-selected');
                nonCustomVariantInputElement.checked = true;
                nonCustomVariantInputElement.dispatchEvent(new Event('change'));
            }
        }

    }

    updateForAttributeForInputs(type) {
        if(type === TYPE_CUSTOMIZABLE) {
            [...this.querySelectorAll('label')].find(label => {
                const inputDefault = this.querySelector(`input#${label.getAttribute('for')}`);
                const inputCustom = this.querySelector(`input#${label.getAttribute('for')}${CUSTOMIZABLE_HANDLE_SUFFIXE}`);

                // Check if an input with the same id + suffixe exists
                if(!inputCustom) return;

                // if option is not available, we don't add the suffixe
                if(inputDefault?.classList.contains('product-variant-option--unavailable')) return;

                // if option already has the correct suffixe, we don't add the suffixe
                if(label.getAttribute('for').includes(CUSTOMIZABLE_HANDLE_SUFFIXE)) return;

                label.setAttribute('for', label.getAttribute('for') + CUSTOMIZABLE_HANDLE_SUFFIXE);
            })
        } else if(type === TYPE_DEFAULT) {
            [...this.querySelectorAll('label')].find(label => {
                label.setAttribute('for', label.getAttribute('for').replaceAll(CUSTOMIZABLE_HANDLE_SUFFIXE, ''));
            })
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if(name === 'data-type' && oldValue !== newValue) {
            this.type = this.getAttribute('data-type');
            this.updateForAttributeForInputs(newValue);
        }
    }

    static get observedAttributes() {
        return ['data-type'];
    }

    onVariantChange() {
        this.updateOptions();
        this.updateCurrentVariant();

        if (!this.currentVariant) {
            this.toggleAddButton(true, '', true);
        } else {
            this.updateURL();
            this.updateVariantInput();
            this.renderProductInfo();
            this.renderProductDescription();
        }

        if(this.type === TYPE_CUSTOMIZABLE) {
            const optionName = this.currentVariant.option1;

            this.querySelectorAll('label').forEach(label => {
                label.classList.remove('force-selected');
            })

            setTimeout(() => {
                this.querySelector(`label[for="variant-contenance-${handleize(optionName)}"]`)?.classList.add('force-selected')
            },150);
        }
    }

    // when a product variant is selected, DOM element needs to be update
    // register here every section to update and don't forget to match their id
    getSectionsToUpdate() {
        const productVariantsSections = this.availableOptions.flatMap(optionHandle => [
            `product-variant-${optionHandle}-${this.sectionId}`,
            `product-variant-mobile-${optionHandle}-${this.sectionId}`
        ]);

        return [
            `product-gallery-${this.sectionId}`,
            `product-tags-${this.sectionId}`,
            `product-title-${this.sectionId}`,
            `product-price-${this.sectionId}`,
            `product-addtocart-${this.sectionId}`,
            `product-sticky-form-${this.sectionId}`,
            `product-bundle-${this.sectionId}`,
            `product-variants-quantite-${this.sectionId}`,
            `product-customization-${this.sectionId}`,
            `product-zoom-${this.sectionId}`,
            `product-delivery-${this.sectionId}`,
            `product-link-${this.sectionId}`,
            ...productVariantsSections
        ]
    }

    // register here every section for update it with an other section both dynamically
    getSectionsDescriptionToUpdate() {
        return [
            `product-variant-dimension`
        ]
    }

    getProductInfoUrl() {
        return `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`
    }

    // fetch information product in a other section, for update the current section
    getProductDescriptionUrl() {
        return `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}-product-description`
    }

    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.selectedOption = fieldsets.map((fieldset) => {
            return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
        });
    }

    updateCurrentVariant() {
        this.currentVariant = this.getVariantData().find((variant) => {
            return !variant.options.map((option, index) => {
                return this.selectedOption[index] === option;
            }).includes(false);
        });
    }

    updateURL() {
        if (!this.currentVariant || this.disableUrlUpdate) return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment`);
        for (const productForm of productForms) {
            const input = productForm.querySelector('input[name="id"]');
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    renderProductInfo() {
        const url = this.getProductInfoUrl();
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const sectionsToUpdate = this.getSectionsToUpdate();

                for (const section of sectionsToUpdate) {
                    const old_section = document.getElementById(section);
                    const new_section = html.getElementById(section);
                    if (old_section && new_section && !old_section.isEqualNode(new_section)) old_section.innerHTML = new_section.innerHTML;
                }

                // lozad
                window.lozadObserver.observe();

                // used in a product bundle form for instance
                if (this.eventOnChange) {
                    this.dispatchEvent(new Event('after:variant:change', { bubbles: true }));
                }
            });
    }

    renderProductDescription() {
        const url = this.getProductDescriptionUrl();
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const sectionsToUpdate = this.getSectionsDescriptionToUpdate();

                for (const section of sectionsToUpdate) {
                    const old_section = document.getElementById(section);
                    const new_section = html.getElementById(section);
                    if (old_section && new_section && !old_section.isEqualNode(new_section)) old_section.innerHTML = new_section.innerHTML;
                }

                // lozad
                window.lozadObserver.observe();

                // used in a product bundle form for instance
                if (this.eventOnChange) {
                    this.dispatchEvent(new Event('after:variant:change', { bubbles: true }));
                }
            });
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
        if (!this.productForm) return;

        // @todo fetch content in case string is different then variantStrings.unavailable
        if (disable) {
            this.addButton.setAttribute('disabled', 'disabled');
            if (text) this.addButtonText.textContent = window.variantStrings.stockAlert;
        } else {
            this.addButton.removeAttribute('disabled');
            if (text) this.addButtonText.textContent = text;
            else this.addButtonText.textContent = window.variantStrings.addToCart;
        }

        if (!modifyClass) return;
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define('variant-radios', VariantRadios);
